"use client"

import Link from "next/link"
import Image from "next/image"
import { useState } from "react"
import { Heart } from "lucide-react"
import { useQuery } from "@tanstack/react-query"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent } from "@/components/ui/card"
import { trpc } from "@/trpc/react"
import { Skeleton } from "@/components/ui/skeleton"
import { useToast } from "@/hooks/use-toast"

type Props = {
  id: string
  title: string
  description: string
  date: string
  tags: string[]
  slug: string
  amazonUrl: string
  rakutenUrl: string
  initialPic: string
  isPaginationPage: boolean
}

async function fetchEyecatch(id: string) {
  const response = await fetch(`/api/notion/eyecatch/${id}`)
  const data = await response.json()
  if (data.eyecatch) {
    return data.eyecatch
  }
  throw new Error("Eyecatch not found")
}

export default function SingleBook({
  id,
  title,
  description,
  date,
  tags,
  slug,
  amazonUrl,
  rakutenUrl,
  initialPic,
}: Props) {
  const { toast } = useToast()
  const [picUrl, setPicUrl] = useState<string>(initialPic)

  // tRPC のお気に入り情報取得
  const favoriteQuery = trpc.book.getBookFavoriteStatus.useQuery(
    { bookId: id },
    { enabled: !!id, staleTime: 5000 },
  )

  // toggleFavorite
  const toggleMutation = trpc.book.toggleFavorite.useMutation({
    onError: (error) => {
      if (error.data?.code === "UNAUTHORIZED") {
        toast({
          title: "ログインが必要です",
          description: "お気に入りに追加するにはログインしてください。",
          variant: "destructive",
        })
      } else {
        toast({
          title: "エラーが発生しました",
          description: "エラーが発生しました。再読み込みをお願いします。",
          variant: "destructive",
        })
        console.error(error)
      }
    },
  })

  // 画像取得
  const { error, isLoading } = useQuery(
    ["eyecatch", id],
    () => fetchEyecatch(id),
    {
      initialData: initialPic,
      onSuccess: (data) => {
        if (typeof data === "string") {
          setPicUrl(data)
        }
      },
    },
  )

  // ハートを押したとき
  const handleToggleFavorite = async () => {
    if (!id) return
    await toggleMutation.mutateAsync({ bookId: id })
    favoriteQuery.refetch()
  }

  // ロード中に大きなスケルトンを表示
  if (isLoading || favoriteQuery.isLoading) {
    return (
      <Skeleton className="w-full h-[400px] lg:h-[300px] md:h-[250px] rounded-md" />
    )
  }

  if (error || favoriteQuery.error) {
    console.error("Error loading image or favorite", error)
    return <div>Error loading data</div>
  }

  const isFavorited = favoriteQuery.data?.isFavorited ?? false
  const favoritesCount = favoriteQuery.data?.count ?? 0

  return (
    <Card className="flex h-full overflow-hidden shadow-md hover:shadow-lg transition-shadow">
      {/* Left Side Image */}
      <Link
        href={`/book/${slug}`}
        className="relative w-1/2 aspect-[3/4] overflow-hidden m-2 rounded-md border border-muted"
      >
        <Image
          src={picUrl}
          alt={title}
          fill
          className="object-cover rounded-md"
        />
      </Link>

      {/* Right Side Content */}
      <div className="w-1/2 flex flex-col justify-start p-1">
        <CardContent className="flex-grow p-1">
          {/* タグ部分 */}
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground mb-2">
            <time dateTime={date} className="mr-2">
              {date}
            </time>
            {tags.map((tag) => (
              <Badge
                key={tag}
                variant="default"
                className="px-2 py-1 rounded-full bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
              >
                #{tag}
              </Badge>
            ))}
          </div>

          {/* タイトル */}
          <h3 className="text-lg font-semibold leading-tight mb-2">
            <Link href={`/book/${slug}`} className="hover:underline">
              {title}
            </Link>
          </h3>

          {/* 説明 */}
          <p className="text-sm text-muted-foreground line-clamp-3">
            {description}
          </p>
        </CardContent>

        {/* ボタン群 */}
        <Button
          asChild
          variant="outline"
          className="flex-1 bg-[#FF9900] text-white hover:bg-[#FF9900]/90 max-h-[40px]"
          size="sm"
        >
          <Link href={amazonUrl}>Amazon</Link>
        </Button>
        <Button
          asChild
          variant="outline"
          className="flex-1 bg-[#BF0000] text-white hover:bg-[#BF0000]/90 max-h-[40px]"
          size="sm"
        >
          <Link href={rakutenUrl}>Rakuten</Link>
        </Button>

        {/* ハートアイコン + お気に入り数 */}
        <div className="flex items-center p-2 gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleToggleFavorite}
            className="text-muted-foreground hover:text-red-500"
          >
            {isFavorited ? (
              <Heart className="h-5 w-5 fill-current text-red-500" />
            ) : (
              <Heart className="h-5 w-5" />
            )}
            <span className="sr-only">いいね</span>
          </Button>
          <span className="text-xs ml-1">
            {favoritesCount === 0
              ? "0人がオススメ"
              : `${favoritesCount}人がオススメ`}
          </span>
        </div>
      </div>
    </Card>
  )
}
